import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Textarea,
} from "@chakra-ui/react";
import { formatISO, parseISO } from "date-fns";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import FlippyStack from "../components/FlippyStack";
import SubmitButton from "../components/SubmitButton";
import { useRegistration } from "../context/Registration";
import { Student } from "../models";
import { validateInThePast } from "../util";

export type StudentFormData = {
  givenNames: string;
  familyName: string;
  birthdate: string;
  specialNotes?: string;
};

type StudentCallback = (student: Student) => void;

type PropTypes = {
  back: () => void;
  continue: () => void;
  onMount?: () => void;
};

const EditStudent = (props: PropTypes) => {
  const { id } = useParams<{ id: string | undefined }>();
  const { students, addStudent, updateStudent, session, generateId } =
    useRegistration();

  const { onMount } = props;
  useEffect(() => {
    onMount && onMount();
  }, [onMount]);

  const currentStudent = id ? students.find((s) => s.id === +id) : undefined;
  const isCreating = !currentStudent;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<StudentFormData>({
    mode: "onChange",
    defaultValues: currentStudent
      ? {
          givenNames: currentStudent.givenNames,
          familyName: currentStudent.familyName,
          birthdate: formatISO(currentStudent.birthdate, {
            representation: "date",
          }),
          specialNotes: currentStudent.specialNotes,
        }
      : {},
  });

  if (!session) {
    props.back();
    return null;
  }

  const onSubmit = (callback: StudentCallback) =>
    handleSubmit(async (data: StudentFormData) => {
      if (isCreating) {
        const student: Student = {
          ...data,
          id: generateId(),
          birthdate: parseISO(data.birthdate),
        };
        addStudent(student);
        callback(student);
      } else {
        const student: Student = {
          ...data,
          id: currentStudent.id,
          birthdate: parseISO(data.birthdate),
        };
        updateStudent(student);
        callback(student);
      }
    });

  return (
    <form onSubmit={onSubmit(props.continue)}>
      {isCreating && (
        <Heading size="md" mb={2}>
          Add a student
        </Heading>
      )}
      {!isCreating && (
        <Heading size="md" mb={2}>
          Editing student
        </Heading>
      )}
      <FlippyStack>
        <FormControl id="givenNames" isRequired isInvalid={!!errors.givenNames}>
          <FormLabel>First name</FormLabel>
          <Input
            bgColor="white"
            type="text"
            {...register("givenNames", { required: true, maxLength: 50 })}
          />

          {/* use role="alert" to announce the error message */}
          {errors.givenNames && errors.givenNames.type === "required" && (
            <FormErrorMessage>This is required.</FormErrorMessage>
          )}
          {errors.givenNames && errors.givenNames.type === "maxLength" && (
            <FormErrorMessage>Maximum length exceeded.</FormErrorMessage>
          )}
        </FormControl>
        <FormControl
          id="familyName"
          isRequired
          isInvalid={!!errors.familyName}
          mt={2}
        >
          <FormLabel>Last name</FormLabel>
          <Input
            bgColor="white"
            type="text"
            {...register("familyName", { required: true, maxLength: 50 })}
          />

          {/* use role="alert" to announce the error message */}
          {errors.familyName && errors.familyName.type === "required" && (
            <FormErrorMessage>This is required.</FormErrorMessage>
          )}
          {errors.familyName && errors.familyName.type === "maxLength" && (
            <FormErrorMessage>Maximum length exceeded.</FormErrorMessage>
          )}
        </FormControl>
        <FormControl id="birthdate" isRequired isInvalid={!!errors.birthdate}>
          <FormLabel>Birthdate</FormLabel>
          <Input
            type="date"
            bgColor="white"
            {...register("birthdate", {
              required: true,
              validate: validateInThePast,
            })}
          />

          {/* use role="alert" to announce the error message */}
          {errors.birthdate?.type === "required" && (
            <FormErrorMessage>This is required.</FormErrorMessage>
          )}
          {errors.birthdate?.type === "validate" && (
            <FormErrorMessage>
              This birthdate is not eligible for taking classes.
            </FormErrorMessage>
          )}
        </FormControl>
      </FlippyStack>
      <FormControl id="specialNotes" isInvalid={!!errors.familyName} mt={2}>
        <FormLabel>
          Anything you would like us to know (e.g. diagnoses, etc.)
        </FormLabel>
        <Textarea
          bgColor="white"
          {...register("specialNotes", { required: false, maxLength: 1000 })}
        />

        {/* use role="alert" to announce the error message */}
        {errors.specialNotes && errors.specialNotes.type === "maxLength" && (
          <FormErrorMessage>Maximum length exceeded.</FormErrorMessage>
        )}
      </FormControl>
      <SubmitButton
        control={control}
        flavourText="One or more fields are empty."
        caption="Save and continue"
      />
    </form>
  );
};

export default EditStudent;
